"use client";

import { Calendar, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/30" />
        <div className="mt-6 h-96 w-full animate-pulse rounded-xl bg-muted/30" />
        <div className="mt-8 space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/30" />
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const author = post.author;
  const categories = post.categories || [];
  const tags = post.tags || [];

  return (
    <article className="mx-auto max-w-4xl px-4 py-12">
      {}
      <h1 className="font-display text-4xl font-bold leading-tight text-foreground md:text-5xl">
        {post.title}
      </h1>

      {}
      <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
              })}
            </time>
          </div>
        )}
        {author && (
          <div className="flex items-center gap-2">
            <User className="size-4" />
            <span>{author.name}</span>
          </div>
        )}
      </div>

      {}
      {(categories.length > 0 || tags.length > 0) && (
        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((cat, i) => (
            <span
              key={cat.id}
              data-index={i}
              className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
            >
              {cat.name}
            </span>
          ))}
          {tags.map((tag, i) => (
            <span
              key={tag.id}
              data-index={i}
              className="rounded-md bg-secondary/10 px-3 py-1 text-xs font-medium text-secondary"
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {}
      {post.featuredImage && (
        <div className="mt-8 overflow-hidden rounded-xl shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      <div
        className="prose prose-lg mt-8 max-w-none text-foreground prose-headings:font-display prose-headings:font-semibold prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12" />

      {}
      {author && (
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center gap-4">
              <Avatar className="size-16">
                <AvatarImage src={author.avatar?.["96"]} alt={author.name} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {author.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-lg">{author.name}</CardTitle>
                <CardDescription>Author</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-64 animate-pulse rounded-xl bg-muted/30" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="overflow-hidden border-border/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-medium">
          {post.featuredImage && (
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-48 w-full object-cover"
            />
          )}
          <CardHeader>
            <CardTitle className="line-clamp-2 text-base">{post.title}</CardTitle>
            {post.excerpt && (
              <CardDescription className="line-clamp-3 text-sm">
                <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {post.link && (
              <Button variant="outline" size="sm" asChild className="w-full">
                <Link to={post.link}>Read Article</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplate() {
  const currentPostId = typeof window !== "undefined" && (window as any).wvcClient
    ? (window as any).wvcClient.getCurrentPostID()
    : null;

  return (
    <div className="bg-background">
      {}
      {currentPostId && (
        <PostsProvider className="border-b border-border/30" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
          <SinglePostContent />
        </PostsProvider>
      )}

      {}
      <section className="bg-surface py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="mb-8 font-display text-3xl font-bold text-foreground">
            Related Articles
          </h2>
          <PostsProvider wp_query={{ per_page: 3, order: "desc", orderby: "date" }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "_embedded", "featured_media", "_links.wp:featuredmedia"]}>
            <RelatedPostsContent />
          </PostsProvider>
        </div>
      </section>
    </div>
  );
}